import type { AccountRole, SessionSnapshot } from "./secure-session";

export type SessionGateState =
  | { kind: "unavailable"; reason: "native-only" | "contract-not-attested" | "auth-boundary-blocked" }
  | { kind: "checking" }
  | { kind: "signedOut" }
  | { kind: "authenticated"; displayName: string | null; role: AccountRole; expiresAt: string }
  | { kind: "error" };

export interface SessionStatusPort {
  status(): Promise<SessionSnapshot>;
}

export interface AuthRuntimePolicy {
  runtimeStatus: string;
  readinessStatus: string;
  blockerCount: number;
}

interface AuthContractProjection {
  source?: { runtimeStatus?: unknown };
  storeReadiness?: { status?: unknown; blockers?: unknown };
}

interface SessionGateOptions {
  isNative: boolean;
  policy: AuthRuntimePolicy;
  session: SessionStatusPort;
  now?: () => number;
}

interface SessionGateRuntimeOptions extends SessionGateOptions {
  onState(state: SessionGateState): void;
  schedule?: (callback: () => void, delay: number) => unknown;
  cancelSchedule?: (handle: unknown) => void;
}

const PRODUCTION_ATTESTED = "production-attested";
const AUTH_READY = "ready";
const MAX_TIMER_DELAY = 2_147_483_647;

export function authRuntimePolicyFromContract(contract: AuthContractProjection): AuthRuntimePolicy {
  return {
    runtimeStatus: typeof contract.source?.runtimeStatus === "string" ? contract.source.runtimeStatus : "invalid",
    readinessStatus: typeof contract.storeReadiness?.status === "string" ? contract.storeReadiness.status : "invalid",
    blockerCount: Array.isArray(contract.storeReadiness?.blockers) ? contract.storeReadiness.blockers.length : 1,
  };
}

export function initialSessionGateState(isNative: boolean, policy: AuthRuntimePolicy): SessionGateState {
  if (!isNative) return { kind: "unavailable", reason: "native-only" };
  if (policy.runtimeStatus !== PRODUCTION_ATTESTED) {
    return { kind: "unavailable", reason: "contract-not-attested" };
  }
  if (policy.readinessStatus !== AUTH_READY || policy.blockerCount !== 0) {
    return { kind: "unavailable", reason: "auth-boundary-blocked" };
  }
  return { kind: "checking" };
}

export async function resolveSessionGate(options: SessionGateOptions): Promise<SessionGateState> {
  const initial = initialSessionGateState(options.isNative, options.policy);
  if (initial.kind !== "checking") return initial;

  try {
    const snapshot = await options.session.status();
    if (snapshot.state === "signedOut") return { kind: "signedOut" };
    if (Date.parse(snapshot.expiresAt) <= (options.now ?? Date.now)()) return { kind: "error" };
    return {
      kind: "authenticated",
      displayName: snapshot.account.displayName,
      role: snapshot.account.role,
      expiresAt: snapshot.expiresAt,
    };
  } catch {
    return { kind: "error" };
  }
}

export function createSessionGateRuntime(options: SessionGateRuntimeOptions): {
  start(): Promise<void>;
  resume(): Promise<void>;
  stop(): void;
} {
  const now = options.now ?? Date.now;
  const schedule = options.schedule ?? ((callback, delay) => window.setTimeout(callback, delay));
  const cancelSchedule = options.cancelSchedule ?? ((handle) => window.clearTimeout(handle as number));
  let scheduled: unknown;
  let stopped = false;
  let generation = 0;

  const cancelExpiryCheck = (): void => {
    if (scheduled !== undefined) cancelSchedule(scheduled);
    scheduled = undefined;
  };

  const reconcile = async (): Promise<void> => {
    const ownGeneration = ++generation;
    cancelExpiryCheck();
    const initial = initialSessionGateState(options.isNative, options.policy);
    options.onState(initial);
    if (initial.kind !== "checking") return;

    const resolved = await resolveSessionGate({ ...options, now });
    if (stopped || ownGeneration !== generation) return;
    options.onState(resolved);
    if (resolved.kind === "authenticated") {
      const delay = Math.min(Math.max(Date.parse(resolved.expiresAt) - now(), 0), MAX_TIMER_DELAY);
      scheduled = schedule(() => { void reconcile(); }, delay);
    }
  };

  return {
    async start() {
      stopped = false;
      await reconcile();
    },
    async resume() {
      if (!stopped) await reconcile();
    },
    stop() {
      stopped = true;
      generation += 1;
      cancelExpiryCheck();
    },
  };
}

export function createSessionGateRuntimeFromContract(
  contract: AuthContractProjection,
  options: Omit<SessionGateRuntimeOptions, "policy">,
): ReturnType<typeof createSessionGateRuntime> {
  return createSessionGateRuntime({
    ...options,
    policy: authRuntimePolicyFromContract(contract),
  });
}
